/**
 * ROSTER-01 (Seth 2026-08-26): the Drive "Employee Roster.pdf" as a LIVE Team
 * pane — who everyone is, what they do, how to reach them. Same grouping as
 * the printed sheet (Office / Service / Install / Sales / New Construction /
 * Plumbing), but with photos, tap-to-call numbers, and a chat button that
 * opens a Pegasus DM. Data = tech_profile (seeded from the PDF, admin-
 * editable right here) + ESC names + app_user for who's chat-reachable.
 * The old PDF's job is done — edit here, never re-print.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Phone, MessageCircle, Pencil, Check, X, Building2, UserPlus, Trash2, AlertTriangle } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/cn";
import { useEntryFormKeys } from "@/lib/keys";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { TechAvatar } from "@/components/TechAvatar";
import { Card } from "@/components/ui/Card";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { InfoTip } from "@/components/InfoTip";

interface RosterRow {
  empno: string;
  display_name: string | null;
  title: string | null;
  cellphone: string | null;
  city: string | null;
  ext: string | null;
  roster_group: string | null;
  email: string | null;
}

const GROUPS = ["Office", "Service", "Install", "Sales", "New Construction", "Plumbing"];
// New employees with no department yet — shown FIRST so nobody gets lost.
const UNASSIGNED = "Unassigned";

interface EscEmp { empno: string; empname: string | null; inactive: number | boolean | null; cellphone?: string | null; phone?: string | null; city?: string | null }

function useRoster() {
  return useQuery({
    queryKey: ["team_roster"],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<{
      rows: (RosterRow & { name: string; chatEmail: string | null; escInactive: boolean })[];
      escEmps: EscEmp[];
    }> => {
      // ROSTER-03 (Seth 2026-08-26: "I'll be adding them directly in pegasus
      // ... I don't want to have to remember to go to the roster"): EVERY
      // active Pegasus employee record (tech_profile, end_date null) is on
      // the roster automatically — no second step, ever. No department yet →
      // they land in "Unassigned" in plain sight, not hidden.
      const [{ data: profiles }, { data: emps }, { data: users }] = await Promise.all([
        supabase.from("tech_profile")
          .select("empno, display_name, title, cellphone, city, ext, roster_group, email")
          .is("end_date", null),
        supabase.from("mirror_employee").select("empno, empname, inactive, cellphone, phone, city"),
        supabase.from("app_user").select("empno, email"),
      ]);
      const escEmps = (emps ?? []) as EscEmp[];
      const byEmp = new Map(escEmps.map((e) => [e.empno, e]));
      const userBy = new Map(((users ?? []) as { empno: string | null; email: string }[])
        .filter((u) => u.empno).map((u) => [u.empno as string, u.email]));
      const rows = ((profiles ?? []) as RosterRow[]).map((p) => {
        const esc = byEmp.get(p.empno);
        return {
          ...p,
          // A blank Pegasus field means "use ESC's" — the Team Directory
          // drawer saves blanks when a value matches ESC (SETTINGS-NAV-02:
          // that was wiping phones and cities off this roster).
          cellphone: p.cellphone ?? esc?.cellphone ?? esc?.phone ?? null,
          city: p.city ?? esc?.city ?? null,
          name: p.display_name ?? esc?.empname ?? p.empno,
          chatEmail: userBy.get(p.empno) ?? null,
          // A real ESC employee flipped inactive in ESC — nudge the admin to
          // remove them here too (we never auto-remove; the roster is curated).
          escInactive: !!esc && (esc.inactive === 1 || esc.inactive === true),
        };
      }).sort((a, b) => a.name.localeCompare(b.name));
      return { rows, escEmps };
    },
  });
}

function useSaveRosterRow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { empno: string } & Partial<Pick<RosterRow, "title" | "cellphone" | "city" | "ext" | "roster_group">> & { end_date?: string | null }) => {
      const { empno, ...patch } = v;
      const { error } = await supabase.from("tech_profile")
        .update({ ...patch, updated_at: new Date().toISOString() }).eq("empno", empno);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["team_roster"] }),
  });
}

/** Add someone — an ESC employee picked from the list, or an office-only
 *  person by name (synthetic R- id, like Holly + Matthew). Upsert, so
 *  RE-adding someone who was removed simply revives their card. */
function useAddRosterRow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      empno: string | null; freeName: string;
      title: string; cellphone: string; ext: string; city: string; roster_group: string;
    }) => {
      const empno = v.empno
        ?? `R-${v.freeName.trim().toUpperCase().replace(/[^A-Z0-9]+/g, "-").slice(0, 24)}`;
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase.from("tech_profile").upsert({
        empno,
        display_name: v.empno ? null : v.freeName.trim(),
        title: v.title.trim() || null,
        cellphone: v.cellphone.trim() || null,
        ext: v.ext.trim() || null,
        city: v.city.trim() || null,
        roster_group: v.roster_group,
        end_date: null,
        updated_by: u?.user?.email ?? null,
        updated_at: new Date().toISOString(),
      }, { onConflict: "empno" });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["team_roster"] }),
  });
}

export function RosterPane() {
  const { data, isLoading } = useRoster();
  const canEdit = useCan("settings.people");
  const [addOpen, setAddOpen] = useState(false);
  const rows = data?.rows ?? [];
  const grouped = useMemo(() => {
    const m = new Map<string, typeof rows>();
    for (const g of [UNASSIGNED, ...GROUPS]) m.set(g, []);
    for (const r of rows) {
      const g = GROUPS.includes(r.roster_group ?? "") ? (r.roster_group as string) : UNASSIGNED;
      m.get(g)!.push(r);
    }
    return m;
  }, [rows]);

  return (
    <div className="space-y-6">
      {canEdit && (
        <div className="flex items-center justify-end gap-3">
          <InfoTip label="About the roster" align="end">Everyone sees the roster. Edit any card with its pencil.</InfoTip>
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <UserPlus className="h-4 w-4" /> Add Person
          </Button>
        </div>
      )}
      {addOpen && (
        <AddPersonModal
          onClose={() => setAddOpen(false)}
          escEmps={data?.escEmps ?? []}
          onRoster={new Set(rows.map((r) => r.empno))}
        />
      )}
      {isLoading && <p className="rounded-xl border border-hairline bg-white py-10 text-center text-sm text-slate-400">Loading the roster…</p>}
      {[UNASSIGNED, ...GROUPS].map((g) => {
        const people = grouped.get(g) ?? [];
        if (people.length === 0) return null;
        return (
          <section key={g}>
            <h3 className={cn("mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider", g === UNASSIGNED ? "text-amber-600" : "text-slate-400")}>
              {g} · {people.length}
              {g === UNASSIGNED && <InfoTip label="About Unassigned">New employees land here. Pick their department with the pencil on their card.</InfoTip>}
            </h3>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {people.map((p) => <RosterCard key={p.empno} p={p} canEdit={canEdit} />)}
            </div>
          </section>
        );
      })}

      {/* The printed sheet's footer — the company card. */}
      <Card className="p-4">
        <h3 className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-800">
          <Building2 className="h-4 w-4 text-brand-600" /> Sedgwick Heating &amp; Air Conditioning
        </h3>
        <div className="grid gap-x-8 gap-y-1.5 text-sm text-slate-600 sm:grid-cols-2">
          <span>Main <a href="tel:9528819000" className="font-semibold text-brand-700 hover:underline">(952) 881-9000</a></span>
          <span>Text (Podium) <a href="sms:9525929895" className="font-semibold text-brand-700 hover:underline">952-592-9895</a></span>
          <span>Fax <span className="font-semibold text-slate-700">952-881-4491</span></span>
          <span>Email <a href="mailto:contact@sedgwickheating.com" className="font-semibold text-brand-700 hover:underline">contact@sedgwickheating.com</a></span>
          <span className="sm:col-span-2">1240 Trapp Rd, Suite A · Eagan, MN 55121</span>
        </div>
      </Card>
    </div>
  );
}

/** ADD — pick a real ESC employee (new hires appear here automatically once
 *  they're in ESC) or type a name for office-only people. */
function AddPersonModal({ onClose, escEmps, onRoster }: {
  onClose: () => void;
  escEmps: EscEmp[];
  onRoster: Set<string>;
}) {
  const add = useAddRosterRow();
  const [empno, setEmpno] = useState("");
  const [freeName, setFreeName] = useState("");
  const [f, setF] = useState({ title: "", cellphone: "", ext: "", city: "", roster_group: "Office" });
  const available = useMemo(
    () => escEmps
      .filter((e) => !onRoster.has(e.empno) && !(e.inactive === 1 || e.inactive === true) && (e.empname ?? "").trim())
      .sort((a, b) => (a.empname ?? "").localeCompare(b.empname ?? "")),
    [escEmps, onRoster],
  );
  const ready = (empno || freeName.trim().length >= 2) && f.roster_group;
  const onFormKeys = useEntryFormKeys(() => { if (ready && !add.isPending) add.mutate({ empno: empno || null, freeName, ...f }, { onSuccess: onClose }); });
  const inp = "w-full rounded-lg border border-hairline px-3 py-2 text-sm focus:border-brand-400 focus:outline-none";
  return (
    <Modal open onClose={onClose} title="Add to the Roster" size="sm">
      <div className="space-y-3" data-entry-form onKeyDown={onFormKeys}>
        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-400">Employee</span>
          <select value={empno} onChange={(e) => { setEmpno(e.target.value); if (e.target.value) setFreeName(""); }} className={inp}>
            <option value="">Choose from ESC…</option>
            {available.map((e) => <option key={e.empno} value={e.empno}>{e.empname}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-400">Or a Name (Not in ESC)</span>
          <input value={freeName} onChange={(e) => { setFreeName(e.target.value); if (e.target.value) setEmpno(""); }}
            placeholder="e.g. Jane Miller" className={inp} />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Title (e.g. CSR)" className={inp} />
          <select value={f.roster_group} onChange={(e) => setF({ ...f, roster_group: e.target.value })} className={inp}>
            {GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
          <input value={f.cellphone} onChange={(e) => setF({ ...f, cellphone: e.target.value })} placeholder="Cell" className={inp} />
          <input value={f.ext} onChange={(e) => setF({ ...f, ext: e.target.value })} placeholder="Ext (office)" className={inp} />
          <input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} placeholder="City of residence" className={cn(inp, "col-span-2")} />
        </div>
        {add.isError && <p className="text-xs font-medium text-rose-600">{(add.error as Error)?.message || "Couldn't add — try again."}</p>}
        <div className="flex justify-end gap-2 border-t border-slate-100 pt-3">
          <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" disabled={!ready || add.isPending}
            onClick={() => add.mutate({ empno: empno || null, freeName, ...f }, { onSuccess: onClose })}>
            <UserPlus className="h-3.5 w-3.5" /> {add.isPending ? "Adding…" : "Add to Roster"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function RosterCard({ p, canEdit }: {
  p: RosterRow & { name: string; chatEmail: string | null; escInactive: boolean };
  canEdit: boolean;
}) {
  const save = useSaveRosterRow();
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState({ title: "", cellphone: "", ext: "", city: "", roster_group: "" });
  const startEdit = () => {
    setF({ title: p.title ?? "", cellphone: p.cellphone ?? "", ext: p.ext ?? "", city: p.city ?? "", roster_group: p.roster_group ?? "Office" });
    setEditing(true);
  };
  const doSave = () => save.mutate(
    { empno: p.empno, title: f.title.trim() || null, cellphone: f.cellphone.trim() || null, ext: f.ext.trim() || null, city: f.city.trim() || null, roster_group: f.roster_group },
    { onSuccess: () => setEditing(false) },
  );
  const openChat = () => {
    if (p.chatEmail) window.dispatchEvent(new CustomEvent("pegasus:open-chat", { detail: { email: p.chatEmail } }));
  };
  const inp = "w-full rounded-lg border border-hairline px-2.5 py-1.5 text-sm focus:border-brand-400 focus:outline-none";

  return (
    <Card className="p-3.5">
      <div className="flex items-start gap-3">
        <TechAvatar empno={p.empno} name={p.name} size="lg" />
        <div className="min-w-0 flex-1">
          {!editing ? (
            <>
              <div className="flex items-start justify-between gap-2">
                <span className="min-w-0">
                  <span className="text-[15px] font-bold leading-tight text-slate-900">{p.name}</span>
                  {canEdit && p.escInactive && (
                    <span className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 align-middle text-[10px] font-bold text-amber-700 ring-1 ring-amber-200"
                      title="ESC marks this employee inactive — remove them from the roster via the pencil if they've left">
                      <AlertTriangle className="h-3 w-3" /> Inactive in ESC
                    </span>
                  )}
                </span>
                <span className="flex shrink-0 items-center gap-0.5">
                  {p.chatEmail && (
                    <button type="button" onClick={openChat} title={`Chat with ${p.name} in Pegasus`}
                      className="rounded-lg p-1.5 text-brand-600 transition-colors hover:bg-brand-50">
                      <MessageCircle className="h-4 w-4" />
                    </button>
                  )}
                  {canEdit && (
                    <button type="button" onClick={startEdit} title="Edit this person's roster info"
                      className="rounded-lg p-1.5 text-slate-300 transition-colors hover:bg-slate-100 hover:text-slate-600">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  )}
                </span>
              </div>
              {p.title && <p className="text-[13px] font-medium text-slate-500">{p.title}</p>}
              <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                {p.cellphone && (
                  <a href={`tel:${p.cellphone.replace(/\D/g, "")}`}
                    className="inline-flex items-center gap-1.5 font-bold tabular-nums text-brand-700 hover:underline">
                    <Phone className="h-3.5 w-3.5" /> {p.cellphone}
                  </a>
                )}
                {p.ext && <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-bold text-slate-600">Ext {p.ext}</span>}
              </div>
              {p.city && <p className="mt-0.5 text-xs text-slate-400">{p.city}</p>}
            </>
          ) : (
            <div className="space-y-1.5">
              <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Title" className={inp} />
              <div className="flex gap-1.5">
                <input value={f.cellphone} onChange={(e) => setF({ ...f, cellphone: e.target.value })} placeholder="Cell" className={cn(inp, "min-w-0 flex-1")} />
                <input value={f.ext} onChange={(e) => setF({ ...f, ext: e.target.value })} placeholder="Ext" className={cn(inp, "w-16")} />
              </div>
              <div className="flex gap-1.5">
                <input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} placeholder="City" className={cn(inp, "min-w-0 flex-1")} />
                <select value={f.roster_group} onChange={(e) => setF({ ...f, roster_group: e.target.value })} className={cn(inp, "w-36")}>
                  {GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-1.5 pt-0.5">
                <button type="button" disabled={save.isPending}
                  title="Take this person off the roster (they can be re-added any time — nothing is deleted)"
                  onClick={() => {
                    if (confirm(`Remove ${p.name} from the roster? Their card disappears for everyone — re-add them any time with Add Person.`)) {
                      save.mutate({ empno: p.empno, end_date: new Date().toISOString().slice(0, 10) }, { onSuccess: () => setEditing(false) });
                    }
                  }}
                  className="rounded-lg px-2 py-1 text-xs font-semibold text-rose-500 transition-colors hover:bg-rose-50">
                  <Trash2 className="mr-0.5 inline h-3.5 w-3.5" />Remove
                </button>
                <span className="flex-1" />
                <button type="button" onClick={() => setEditing(false)} className="rounded-lg px-2.5 py-1 text-xs font-semibold text-slate-500 hover:bg-slate-100"><X className="mr-0.5 inline h-3.5 w-3.5" />Cancel</button>
                <button type="button" disabled={save.isPending} onClick={doSave}
                  className="rounded-lg bg-brand-700 px-2.5 py-1 text-xs font-bold text-white hover:bg-brand-800 disabled:bg-brand-300">
                  <Check className="mr-0.5 inline h-3.5 w-3.5" />{save.isPending ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
